import createClient from "openapi-fetch";

import type { paths } from "./generated/schema";
import type { CollectionOut, CreateRoomIn, FilmCard, Filters, FiltersOut } from "./types";

/**
 * Ошибка API. `code` — машинное значение из бэкенда (`NOT_SUBSCRIBED`,
 * `INVALID_INIT_DATA`, `COLLECTION_NOT_FOUND`…), либо `NETWORK` / `VALIDATION_ERROR` / `UNKNOWN`.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`API ${status}: ${code}`);
    this.name = "ApiError";
  }

  get isNotSubscribed(): boolean {
    return this.status === 403 && this.code === "NOT_SUBSCRIBED";
  }
}

export interface ApiOptions {
  baseUrl?: string;
  getInitData: () => string;
  fetch?: (input: Request) => Promise<Response>;
}

interface Result<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

function errorCode(error: unknown, status: number): string {
  if (typeof error === "object" && error !== null && "detail" in error) {
    const detail = error.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return "VALIDATION_ERROR";
  }
  return status === 0 ? "NETWORK" : "UNKNOWN";
}

export function createApi(options: ApiOptions) {
  const client = createClient<paths>({
    baseUrl: options.baseUrl ?? "",
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });
  client.use({
    onRequest({ request }) {
      request.headers.set("X-Telegram-InitData", options.getInitData());
      return request;
    },
  });

  async function unwrap<T>(call: Promise<Result<T>>): Promise<T> {
    let result: Result<T>;
    try {
      result = await call;
    } catch {
      throw new ApiError(0, "NETWORK");
    }
    if (result.data === undefined) {
      throw new ApiError(result.response.status, errorCode(result.error, result.response.status));
    }
    return result.data;
  }

  return {
    health: () => unwrap(client.GET("/api/health")),
    /** Проверка подписки в обход кэша — для кнопки «Проверить подписку». */
    checkSubscription: async (): Promise<boolean> =>
      (await unwrap(client.POST("/api/subscription/check"))).subscribed,
    filters: (): Promise<FiltersOut> => unwrap(client.GET("/api/filters")),
    collections: (): Promise<CollectionOut[]> => unwrap(client.GET("/api/collections")),
    createRoom: async (body: CreateRoomIn): Promise<string> =>
      (await unwrap(client.POST("/api/rooms", { body }))).room,
    roomExists: async (roomId: string): Promise<boolean> =>
      (
        await unwrap(
          client.GET("/api/rooms/{room_id}/exists", { params: { path: { room_id: roomId } } }),
        )
      ).exists,
    soloDeck: async (filters: Filters): Promise<FilmCard[]> =>
      (await unwrap(client.POST("/api/solo/deck", { body: filters }))).deck,
    soloCollectionDeck: async (collectionId: string): Promise<FilmCard[]> =>
      (
        await unwrap(
          client.GET("/api/solo/collections/{collection_id}/deck", {
            params: { path: { collection_id: collectionId } },
          }),
        )
      ).deck,
  };
}

export type Api = ReturnType<typeof createApi>;
